import { useEffect, useRef, useState } from 'react';
import { Box, Chip, Dialog, Stack, Typography } from '@mui/material';
import {
  PRODUKTY,
  adresProduktu,
  dostepnoscProduktu,
  OPIS_DOSTEPNOSCI,
  type Produkt,
} from '@/config/ekosystem';

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * LAUNCHER PRODUKTÓW — poziom 1 nawigacji
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Dlaczego osobny launcher, a nie kolejne pozycje w pasku bocznym: pasek boczny odpowiada na
 * pytanie „gdzie w TYM narzędziu", launcher na pytanie „którego narzędzia użyć". Wrzucenie CRM
 * i Drive do tej samej listy co „Sprzedaż" i „Dania" mówi użytkownikowi, że to zakładki jednego
 * produktu — a to nieprawda i prowadzi do złych oczekiwań (np. że wylogowanie działa wspólnie).
 *
 * Dlaczego duże kafle, a nie lista odnośników: produktów jest sześć i będzie ich więcej, każdy
 * potrzebuje nazwy, zadania i STATUSU. Sześć małych linków w nagłówku nie pomieści statusu,
 * a bez statusu launcher obiecuje przejścia, których nie ma.
 *
 * UCZCIWOŚĆ ZAMIAST WYGODY: kafel bez zweryfikowanego adresu NIE jest odnośnikiem. Jest kaflem
 * nieaktywnym z podaną przyczyną. Odnośnik prowadzący w pustkę psuje zaufanie do całego panelu
 * bardziej, niż pomaga jego obecność.
 */

function KafelProduktu({ p, onZamknij }: { p: Produkt; onZamknij: () => void }) {
  const adres = adresProduktu(p);
  const dostepnosc = dostepnoscProduktu(p);
  const klikalny = Boolean(adres) && !p.biezacy;

  const wspolne = {
    display: 'flex',
    gap: 1.75,
    p: 2,
    borderRadius: 'var(--r-karta)',
    background: p.biezacy ? 'rgba(247, 177, 11, 0.12)' : 'var(--powierzchnia-2)',
    border: p.biezacy ? '1px solid var(--zloto)' : '1px solid transparent',
    textDecoration: 'none',
    color: 'inherit',
    textAlign: 'left' as const,
    width: '100%',
    transition: 'transform var(--czas-szybko), box-shadow var(--czas-szybko), background var(--czas-szybko)',
    ...(klikalny
      ? { '&:hover': { transform: 'translateY(-3px)', boxShadow: 'var(--cien-2)', background: 'var(--powierzchnia)' } }
      : {}),
    ...(!klikalny && !p.biezacy ? { opacity: 0.62 } : {}),
  };

  const tresc = (
    <>
      <Box
        aria-hidden
        sx={{
          width: 46,
          height: 46,
          flex: 'none',
          borderRadius: 'var(--r-maly)',
          background: p.akcent,
          color: 'var(--atrament)',
          display: 'grid',
          placeItems: 'center',
          fontFamily: 'var(--krój-display)',
          fontSize: 17,
        }}
      >
        {p.monogram}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography sx={{ fontWeight: 800, fontSize: 15 }}>{p.nazwa}</Typography>
          {p.biezacy ? (
            <Chip size="small" label="tu jesteś" color="primary" sx={{ height: 20, fontSize: 10.5 }} />
          ) : null}
        </Stack>
        <Typography variant="body2" sx={{ color: 'var(--tekst-2)', mt: 0.25 }}>
          {p.rola}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography sx={{ fontSize: 11, color: dostepnosc === 'DOSTEPNY' ? 'var(--sukces)' : 'var(--tekst-slaby)', fontWeight: 700 }}>
            {p.biezacy ? 'bieżąca aplikacja' : OPIS_DOSTEPNOSCI[dostepnosc]}
          </Typography>
          {/* Wspólnego logowania między produktami dziś NIE MA. Mówimy to wprost przy kaflu,
              bo inaczej użytkownik odbierze przejście jako awarię, a nie jako stan faktyczny. */}
          {klikalny && p.uwierzytelnianie === 'OSOBNE_LOGOWANIE' ? (
            <Typography sx={{ fontSize: 11, color: 'var(--tekst-slaby)' }}>· wymaga osobnego logowania</Typography>
          ) : null}
        </Stack>
      </Box>
    </>
  );

  if (klikalny && adres) {
    return (
      <Box
        component="a"
        href={adres}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onZamknij}
        sx={wspolne}
      >
        {tresc}
      </Box>
    );
  }

  return (
    <Box sx={wspolne} aria-disabled={!p.biezacy}>
      {tresc}
    </Box>
  );
}

export function LauncherProduktow() {
  const [otwarty, setOtwarty] = useState(false);
  const przycisk = useRef<HTMLButtonElement>(null);

  // Skrót klawiszowy — launcher jest używany dziesiątki razy dziennie i sięganie po niego
  // myszą przez cały ekran jest realnym kosztem.
  useEffect(() => {
    function naKlawisz(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        setOtwarty((v) => !v);
      }
    }
    window.addEventListener('keydown', naKlawisz);
    return () => window.removeEventListener('keydown', naKlawisz);
  }, []);

  return (
    <>
      <Box
        component="button"
        ref={przycisk}
        type="button"
        onClick={() => setOtwarty(true)}
        aria-haspopup="dialog"
        aria-expanded={otwarty}
        aria-label="Aplikacje TuSzamy"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          width: '100%',
          px: 2,
          py: 2.5,
          minHeight: 84,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          textAlign: 'left',
          transition: 'background var(--czas-szybko)',
          '&:hover': { background: 'rgba(255,255,255,0.06)' },
        }}
      >
        <Box
          aria-hidden
          sx={{
            width: 40,
            height: 40,
            flex: 'none',
            borderRadius: '12px',
            background: 'var(--zloto)',
            color: 'var(--atrament)',
            display: 'grid',
            placeItems: 'center',
            fontFamily: 'var(--krój-display)',
            fontSize: 19,
            letterSpacing: '-0.04em',
          }}
        >
          TS
        </Box>
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          <Typography sx={{ fontFamily: 'var(--krój-display)', fontSize: 19, lineHeight: 1, color: '#fff' }}>
            TuSzama
          </Typography>
          <Typography sx={{ fontSize: 9.5, letterSpacing: '0.28em', color: 'var(--zloto)', fontWeight: 700, mt: 0.4 }}>
            CONTROL CENTER
          </Typography>
        </Box>
        <Box aria-hidden sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 3px)', gap: '3px', opacity: 0.6 }}>
          {Array.from({ length: 9 }).map((_, i) => (
            <Box key={i} sx={{ width: 3, height: 3, borderRadius: '1px', background: '#fff' }} />
          ))}
        </Box>
      </Box>

      <Dialog
        open={otwarty}
        onClose={() => setOtwarty(false)}
        maxWidth="md"
        fullWidth
        // MUI nadaje role="dialog" elementowi Paper, a nie korzeniowi — `aria-label` postawiony
        // na <Dialog> ląduje więc na warstwie BEZ roli i okno zostaje bez dostępnej nazwy.
        // Czytnik ekranu ogłasza wtedy „okno dialogowe" bez informacji, czego dotyczy.
        aria-labelledby="naglowek-launchera"
        slotProps={{
          paper: {
            'aria-label': 'Aplikacje TuSzamy',
            sx: { borderRadius: 'var(--r-duzy)', background: 'var(--powierzchnia)' },
          },
        }}
      >
        <Box sx={{ p: { xs: 2.5, md: 4 } }}>
          <Typography variant="overline" sx={{ color: 'var(--tekst-slaby)' }}>
            Ekosystem TuSzamy
          </Typography>
          <Typography id="naglowek-launchera" variant="h5" sx={{ mb: 0.5 }}>
            Aplikacje TuSzamy
          </Typography>
          <Typography variant="body2" sx={{ color: 'var(--tekst-2)', mb: 3, maxWidth: '62ch' }}>
            To są osobne produkty, a nie zakładki Control Center. Każdy ma własne konta — przejście
            między nimi oznacza dziś ponowne logowanie. Wspólne logowanie jest osobnym etapem.
          </Typography>

          <Box
            className="sygnal-kaskada"
            sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}
          >
            {PRODUKTY.map((p) => (
              <KafelProduktu key={p.id} p={p} onZamknij={() => setOtwarty(false)} />
            ))}
          </Box>

          <Typography variant="body2" sx={{ color: 'var(--tekst-slaby)', mt: 3, fontSize: 12.5 }}>
            Kafel bez odnośnika nie jest usterką: dla tego produktu nie ma potwierdzonego adresu
            w bieżącym środowisku. Nie podstawiamy adresu „na oko" — domena TuSzamy odpowiada na
            dowolną nazwę, więc zgadnięty odnośnik prowadziłby donikąd.
          </Typography>
        </Box>
      </Dialog>
    </>
  );
}
